import { useCallback } from 'react';
import { cn } from '@discover-lib/utils';
import formatTime from '@discover-data/stores/restaurant/model/times/format';
import useEndTime from '@discover-data/stores/hooks/times/end/useEndTime';
import useStartTime from '@discover-data/stores/hooks/times/start/useStartTime';
import useTimeRangeDefaults from '@discover-data/stores/hooks/times/useTimeRangeDefaults';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import DoubleRangeSlider from './doubleRangeSlider';

export type TimeRangeFilterProps = React.ComponentPropsWithoutRef<'div'>;

export default function TimeRangeFilter({ className, ...props }: TimeRangeFilterProps) {
  const [startTime, setStartTime] = useStartTime();
  const [endTime, setEndTime] = useEndTime();
  const { track } = useAnalytics();
  const { start: DEFAULT_START_HOUR, end: DEFAULT_END_HOUR } = useTimeRangeDefaults();

  const handleSliderChange = useCallback(
    (values: { startMinute: number | null; endMinute: number | null }) => {
      const { startMinute: newStart, endMinute: newEnd } = values;

      const updateTime = (
        time: number | null,
        setter: (val: number | null) => void,
        key: 'start' | 'end'
      ) => {
        const isStart = key === 'start';
        const previousTime = isStart ? startTime : endTime;
        const defaultTime = isStart ? DEFAULT_START_HOUR : DEFAULT_END_HOUR;
        const otherTime = isStart ? endTime : startTime;

        // Convert to decimal hours with half-hour precision
        const timeInHours = time !== null ? Math.round((time / 60) * 2) / 2 : null;
        const previousTimeInHours = previousTime;

        if (timeInHours !== previousTimeInHours) {
          setter(timeInHours);
          track('button_clicked', {
            button_name: 'time range slider',
            button_type: 'selection',
            button_value: `${isStart ? (timeInHours ?? defaultTime) : (otherTime ?? DEFAULT_START_HOUR)}-${isStart ? (otherTime ?? DEFAULT_END_HOUR) : (timeInHours ?? defaultTime)}`,
          });
        }
      };

      updateTime(newStart, setStartTime, 'start');
      updateTime(newEnd, setEndTime, 'end');
    },
    [startTime, endTime, track, setStartTime, setEndTime]
  );

  return (
    <div className={cn('flex flex-col gap-5', className)} {...props}>
      <div className="flex items-center justify-between">
        <h3 className="font-bold">Time Range</h3>
        <span className="text-sm text-muted-foreground">
          {formatTime(startTime ?? DEFAULT_START_HOUR)} - {formatTime(endTime ?? DEFAULT_END_HOUR)}
        </span>
      </div>
      <DoubleRangeSlider
        minMax={{
          startMinute: DEFAULT_START_HOUR * 60,
          endMinute: DEFAULT_END_HOUR * 60,
        }}
        defaultValue={{
          startMinute: (startTime ?? DEFAULT_START_HOUR) * 60,
          endMinute: (endTime ?? DEFAULT_END_HOUR) * 60,
        }}
        onValueChange={handleSliderChange}
        className="mb-9"
      />
    </div>
  );
}
